import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Clock, Menu, X, MapPin, Instagram, ChevronDown, Wrench, BookOpen } from 'lucide-react';
import { companyInfo, cityPages, serviceTypes } from '../data/mock';
import { Button } from './ui/button';

// TikTok icon (pas dans lucide-react)
const TikTokIcon = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: size, height: size }}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [citiesMenuOpen, setCitiesMenuOpen] = useState(false);
  const [servicesMenuOpen, setServicesMenuOpen] = useState(false);

  const navLinks = [
    { href: '#services', label: 'Nos Services' },
    { href: '#clients', label: 'Nos Clients' },
    { href: '#tarifs', label: 'Tarifs' },
    { href: '#avis', label: 'Avis Clients' },
    { href: '#faq', label: 'FAQ' },
    { href: '#contact', label: 'Contact' },
  ];

  const guideLink = { href: '/guide-debouchage', label: 'Guide Débouchage', isRoute: true };

  const serviceLinks = [
    { slug: 'inspection-camera', name: 'Inspection Caméra Canalisation' },
    { slug: 'entretien-bac-graisse', name: 'Entretien Bac à Graisse' },
    { slug: 'pompe-relevage', name: 'Entretien Pompe de Relevage' },
    { slug: 'curage-haute-pression', name: 'Curage Haute Pression' },
    { slug: 'plombier-debouchage', name: 'Plombier Débouchage' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-sky-700 to-teal-600 text-white py-1.5 md:py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center text-sm md:text-base gap-2">
          <div className="flex items-center gap-6 flex-wrap">
            <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-sky-200 transition-colors min-h-[44px]">
              <Phone size={18} />
              <span className="font-semibold text-base">{companyInfo.phone}</span>
            </a>
            <a href={`mailto:${companyInfo.email}`} className="hidden sm:flex items-center gap-2 hover:text-sky-200 transition-colors min-h-[44px]">
              <Mail size={18} />
              <span className="text-sm md:text-base">{companyInfo.email}</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            {/* Social Media Icons */}
            <div className="hidden sm:flex items-center gap-3">
              <a 
                href={companyInfo.social?.instagram || "#"} 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-pink-300 transition-colors p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a 
                href={companyInfo.social?.tiktok || "#"} 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-white transition-colors p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="TikTok"
              >
                <TikTokIcon size={20} />
              </a>
            </div>
            <span className="hidden sm:block w-px h-4 bg-white/30" />
            <span className="flex items-center gap-2 min-h-[44px]">
              <Clock size={18} />
              <span className="font-semibold text-lime-300 text-base">{companyInfo.availability}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-white/95 backdrop-blur-md shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-2 md:py-3">
          <div className="flex items-center justify-between">
            {/* Logo avec slogan */}
            <Link to="/" className="flex items-center gap-2 md:gap-3 group">
              <img 
                src={companyInfo.logo} 
                alt={companyInfo.name}
                className="h-12 md:h-20 w-auto object-contain mix-blend-multiply transition-transform group-hover:scale-105"
              />
              <div className="flex flex-col">
                <span 
                  className="font-bold text-xs sm:text-base lg:text-lg"
                  style={{ 
                    transform: 'rotate(-2deg)', 
                    display: 'inline-block',
                    fontFamily: "'Georgia', serif",
                    fontStyle: 'italic',
                    color: '#0ea5e9'
                  }}
                >
                  Votre Déboucheur
                </span>
                <span 
                  className="font-bold text-sm sm:text-lg lg:text-xl -mt-1"
                  style={{ 
                    transform: 'rotate(-2deg)', 
                    display: 'inline-block',
                    fontFamily: "'Georgia', serif",
                    fontStyle: 'italic',
                    background: 'linear-gradient(90deg, #84cc16, #22c55e)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  c'est nous !
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-gray-700 hover:text-sky-600 font-medium transition-colors relative group"
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-sky-600 group-hover:w-full transition-all duration-300" />
                </a>
              ))}
              
              {/* Lien Guide Débouchage */}
              <Link
                to={guideLink.href}
                className="flex items-center gap-1 text-lime-600 hover:text-lime-700 font-semibold transition-colors relative group"
              >
                <BookOpen size={16} />
                {guideLink.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-lime-600 group-hover:w-full transition-all duration-300" />
              </Link>
              
              {/* Menu déroulant Services/Prestations - Simplifié */}
              <div 
                className="relative"
                onMouseEnter={() => setServicesMenuOpen(true)}
                onMouseLeave={() => setServicesMenuOpen(false)}
              >
                <button className="flex items-center gap-1 text-gray-700 hover:text-sky-600 font-medium transition-colors">
                  <Wrench size={16} />
                  Prestations
                  <ChevronDown size={16} className={`transition-transform ${servicesMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {servicesMenuOpen && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-2xl border border-slate-100 py-2 min-w-[280px] z-50">
                    {serviceLinks.map((service) => (
                      <Link
                        key={service.slug}
                        to={`/${service.slug}`}
                        className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors"
                      >
                        <Wrench size={18} className="text-sky-500" />
                        <span className="font-medium">{service.name}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Menu déroulant Villes */}
              <div 
                className="relative"
                onMouseEnter={() => setCitiesMenuOpen(true)}
                onMouseLeave={() => setCitiesMenuOpen(false)}
              >
                <button className="flex items-center gap-1 text-gray-700 hover:text-sky-600 font-medium transition-colors">
                  <MapPin size={16} />
                  Nos Villes
                  <ChevronDown size={16} className={`transition-transform ${citiesMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {citiesMenuOpen && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-2xl border border-slate-100 py-3 min-w-[200px] z-50">
                    {cityPages.map((city) => (
                      <Link
                        key={city.slug}
                        to={`/debouchage-${city.slug}`}
                        className="block px-4 py-2 text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors"
                      >
                        {city.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </nav>

            {/* CTA Button */}
            <div className="hidden md:flex items-center gap-3">
              <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}>
                <Button className="bg-lime-500 hover:bg-lime-600 text-white font-bold px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2">
                  <Phone size={18} />
                  Appeler
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-3 text-gray-700 hover:text-sky-600 transition-colors min-w-[48px] min-h-[48px] flex items-center justify-center"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 py-4 px-4 shadow-lg max-h-[80vh] overflow-y-auto">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-gray-700 hover:text-sky-600 font-medium py-3 px-4 rounded-lg hover:bg-sky-50 transition-all text-base min-h-[48px] flex items-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              
              {/* Lien Guide Débouchage - Mobile */}
              <Link
                to={guideLink.href}
                className="flex items-center gap-2 text-lime-600 hover:text-lime-700 font-semibold py-3 px-4 rounded-lg bg-lime-50 hover:bg-lime-100 transition-all text-base min-h-[48px]"
                onClick={() => setMobileMenuOpen(false)}
              >
                <BookOpen size={20} />
                {guideLink.label}
              </Link>
              
              {/* Prestations dans le menu mobile - Simplifié */}
              <div className="border-t border-gray-100 pt-3 mt-2">
                <p className="text-base font-semibold text-gray-500 px-4 mb-2 flex items-center gap-2">
                  <Wrench size={18} />
                  Nos Prestations
                </p>
                {serviceLinks.map((service) => (
                  <Link
                    key={service.slug}
                    to={`/${service.slug}`}
                    className="flex items-center gap-3 text-gray-700 hover:text-sky-600 font-medium py-3 px-4 rounded-lg hover:bg-sky-50 transition-all text-base min-h-[48px]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Wrench size={18} className="text-sky-500" />
                    {service.name}
                  </Link>
                ))}
              </div>

              {/* Villes dans le menu mobile */}
              <div className="border-t border-gray-100 pt-3 mt-2">
                <p className="text-base font-semibold text-gray-500 px-4 mb-2 flex items-center gap-2">
                  <MapPin size={18} />
                  Débouchage par ville
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {cityPages.map((city) => (
                    <Link
                      key={city.slug}
                      to={`/debouchage-${city.slug}`}
                      className="text-gray-700 hover:text-sky-600 font-medium py-3 px-4 rounded-lg hover:bg-sky-50 transition-all text-base min-h-[48px] flex items-center"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {city.name}
                    </Link>
                  ))}
                </div>
              </div>
              
              <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="mt-3">
                <Button className="w-full bg-lime-500 hover:bg-lime-600 text-white font-bold py-4 rounded-full text-lg min-h-[52px]">
                  <Phone size={20} className="mr-2" />
                  Appeler maintenant
                </Button>
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;